import { Link } from "react-router-dom";
import { ShoppingBag, Palette, Info, ArrowRight } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { Motif } from "../components/Motifs";
import { PRODUCTS, PAINTINGS } from "../data/products";

export default function Products() {
  return (
    <div data-testid="products-page">
      <PageHeader
        testId="products-header"
        motif="ring"
        eyebrow="Termékek & művészet"
        title={<>Utókezelés & <span className="italic text-[#C04B58]">kortárs művészet.</span></>}
        description="Ami a szalonban megvásárolható: gondosan válogatott utókezelő termékek és kortárs festők alkotásai."
      />

      {/* TERMÉKEK */}
      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8" data-testid="products-section">
        <Reveal>
          <div className="flex items-center gap-3">
            <ShoppingBag size={18} className="text-[#9A3542]" />
            <p className="eyebrow">A szalonban kapható</p>
          </div>
          <h2 className="font-display mt-4 text-3xl font-semibold text-white sm:text-4xl">Tetoválás utókezelés</h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-zinc-400">
            A gyógyulás legalább annyira számít, mint maga a munka — ezért a stúdióban az általunk is használt,
            bevált utókezelő krémeket és kenőcsöket kínáljuk.
          </p>
        </Reveal>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {PRODUCTS.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1}>
              <article className="card-dark group h-full overflow-hidden" data-testid={`product-card-${p.id}`}>
                <div className="relative flex aspect-[4/3] items-center justify-center bg-[#0f0f0f]">
                  <div className="flex flex-col items-center gap-3">
                    <Motif type="flower" className="w-12 text-[#9A3542] opacity-60" />
                    <span className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-zinc-500" data-testid={`product-soon-${p.id}`}>
                      Hamarosan érkezik
                    </span>
                  </div>
                  <span className="absolute left-4 top-4 rounded-full border border-[#9A3542]/60 bg-black/60 px-3 py-1 font-mono2 text-[9px] uppercase tracking-[0.25em] text-[#C04B58] backdrop-blur-sm">
                    Szalonban kapható
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-xl font-semibold text-white">{p.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-400">{p.desc}</p>
                  <p className="mt-4 font-mono2 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    Ár és készlet: érdeklődj a szalonban
                  </p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FESTMÉNYEK */}
      <section className="border-t border-[#202023] bg-[#0F0F11] py-24" data-testid="paintings-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="flex items-center gap-3">
              <Palette size={18} className="text-[#9A3542]" />
              <p className="eyebrow">Művészet a falakon</p>
            </div>
            <h2 className="font-display mt-4 text-3xl font-semibold text-white sm:text-4xl">
              Kortárs festmények <span className="italic text-[#C04B58]">a szalonban.</span>
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-zinc-400">
              A stúdió falain kortárs festők alkotásai kapnak helyet. Az első festmény már megtekinthető nálunk —
              a továbbiak készülőben vannak.
            </p>
          </Reveal>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {PAINTINGS.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.1}>
                <article className="card-dark group relative aspect-[4/5] overflow-hidden" data-testid={`painting-card-${p.id}`}>
                  {p.status === "shown" ? (
                    <div className="relative flex h-full flex-col items-center justify-center gap-4 bg-[#0f0f0f] p-6 text-center">
                      <Motif type="flash" className="w-14 text-[#9A3542] opacity-50" />
                      <p className="font-display text-lg font-semibold text-white">{p.title}</p>
                      <span
                        className="rounded-full bg-[#9A3542] px-3 py-1 font-mono2 text-[9px] uppercase tracking-[0.25em] text-white"
                        data-testid="painting-badge-shown"
                      >
                        Már a szalonban
                      </span>
                      <span className="font-mono2 text-[9px] uppercase tracking-[0.3em] text-zinc-500">
                        Hamarosan érkezik
                      </span>
                    </div>
                  ) : (
                    <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
                      <Motif type="flower" className="w-16 text-[#9A3542] opacity-50" />
                      <p className="font-display text-lg font-semibold text-white">{p.title}</p>
                      <span
                        className="rounded-full border border-white/10 px-3 py-1 font-mono2 text-[9px] uppercase tracking-[0.25em] text-zinc-500"
                        data-testid={`painting-badge-wip-${p.id}`}
                      >
                        Készülőben
                      </span>
                    </div>
                  )}
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10">
            <div
              className="flex flex-col items-start justify-between gap-5 rounded-2xl border border-[#43262C]/60 bg-[#9A3542]/5 p-6 sm:flex-row sm:items-center"
              data-testid="products-note"
            >
              <div className="flex items-start gap-4">
                <Info size={18} className="mt-0.5 shrink-0 text-[#C04B58]" />
                <p className="text-sm leading-relaxed text-zinc-300">
                  A termékek és festmények személyesen, a szalonban tekinthetők meg és vásárolhatók meg. Kérdés
                  esetén írj vagy hívj minket bátran.
                </p>
              </div>
              <Link to="/kapcsolat" data-testid="products-contact-btn" className="btn-ghost shrink-0 !px-5 !py-2.5 !text-[11px]">
                Kapcsolat <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
